import type { RefObject } from 'react';

import { useComputedPagePaddingTopPx } from '@app/offset-anchor';
import { useComputedStylePx } from '@hooks/use-computed-style-px';
import { useIsPinned } from '@hooks/use-is-pinned';
import { useStickyAnchor } from '@hooks/use-sticky-anchor';

export interface UseStickyTableHeaderOptions {
  /**
   * Non-sticky sentinel rendered at the header's natural top; drives the
   * pinned state.
   */
  sentinelRef: RefObject<HTMLElement>;
  /**
   * Whether the header should stick, or a ref to the element it sticks beneath.
   */
  stickyHeader?: boolean | RefObject<HTMLElement>;
  /**
   * Table root that receives the published `--table-sticky-inset-top` variable.
   */
  tableRef: RefObject<HTMLElement>;
}

export interface StickyTableHeader {
  /**
   * Whether the column header is currently pinned to the sticky boundary.
   */
  isColumnHeaderPinned: boolean;
  /**
   * Combined sticky offset in px (page anchor plus the list-actions header).
   */
  stickyInsetTop: number;
}

/**
 * Coordinates the sticky-header offset and pinned state, publishing
 * `--table-sticky-inset-top` and folding in `--list-actions-header-height`.
 */
export const useStickyTableHeader = ({
  sentinelRef,
  stickyHeader,
  tableRef,
}: UseStickyTableHeaderOptions): StickyTableHeader => {
  const computedPagePadding = useComputedPagePaddingTopPx({
    enabled: !!stickyHeader,
  });

  const stickyAnchorTop = useStickyAnchor({
    enabled: !!stickyHeader,
    fallbackPx: computedPagePadding,
    publishTarget: tableRef,
    publishVar: '--table-sticky-inset-top',
    reference: typeof stickyHeader === 'object' ? stickyHeader : undefined,
  });

  const listActionsHeaderHeight = useComputedStylePx(
    tableRef,
    '--list-actions-header-height',
  );

  const stickyInsetTop = stickyAnchorTop + listActionsHeaderHeight;

  const isColumnHeaderPinned = useIsPinned(sentinelRef, stickyInsetTop);

  return { isColumnHeaderPinned, stickyInsetTop };
};

export default useStickyTableHeader;
